import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { FormControl } from '@angular/forms';
import type {
  CategoryPageViewUser,
  ProjectViewUser,
  SearchUser,
} from '@freelancer/datastore/collections';
import { FreelancerReputation } from '@freelancer/datastore/collections';
import { FlagSize } from '@freelancer/ui/flag';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { QueryParams } from '@freelancer/ui/link';
import { Margin } from '@freelancer/ui/margin';
import { FontType, FontWeight, TextSize } from '@freelancer/ui/text';
import { UsernameBadges, UsernameSize } from '@freelancer/ui/username';

// Exclusive use for My List.
@Component({
  selector: 'fl-user-info-my-list',
  template: `<ng-content></ng-content>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserInfoMyListComponent {}

@Component({
  selector: 'fl-user-info',
  template: `
    <div class="UsernameWrapper">
      <fl-username
        flTrackingLabel="RedirectToProfile"
        flTrackingSection="SearchUsersResultCardDesktop"
        [flMarginBottomDesktop]="usernameMarginBottomDesktop"
        [badges]="userBadges"
        [link]="userLink"
        [queryParams]="userLinkQueryParams"
        [displayName]="isBidCard(user) ? user.displayName : user.publicName"
        [username]="user.username"
        [size]="usernameSize"
        [sizeDesktop]="usernameSizeDesktop"
        [truncateText]="true"
      ></fl-username>
      <div class="MyList">
        <ng-content select="fl-user-info-my-list"></ng-content>
      </div>
    </div>
    <div class="Container">
      <div class="Userinfo">
        <ng-container *ngIf="!isNewFreelancer || isBidder">
          <div
            *ngIf="isSearchCard(user) || isBidCard(user)"
            [flMarginBottom]="'xxxsmall'"
            [flMarginBottomDesktop]="'none'"
            [flMarginRight]="'xxsmall'"
            [matTooltip]="ratingTooltipMessage"
            [matTooltipDisabled]="!showTooltip"
          >
            <fl-rating
              class="UserRating"
              [compactTablet]="true"
              [control]="ratingControl"
              [halfRating]="true"
              [size]="IconSize.SMALL"
              [sizeDesktop]="IconSize.MID"
              [readOnly]="true"
            ></fl-rating>
          </div>
          <fl-review-count
            *ngIf="hasReputation(user)"
            [matTooltip]="reviewTooltipMessage"
            [matTooltipDisabled]="!showTooltip"
            [counter]="
              isSearchCard(user)
                ? user.reviews
                : user.reputation?.entireHistory.reviews
            "
            [flMarginBottom]="'xxxsmall'"
            [flMarginBottomDesktop]="'none'"
            [flMarginRight]="'xxsmall'"
            [size]="IconSize.SMALL"
            [sizeDesktop]="IconSize.MID"
          ></fl-review-count>
          <!--
            Hide earnings score for TKN projects, e.g. Deloitte internal and
            insource projects
          -->
          <fl-earnings
            *ngIf="showEarningScore && hasReputation(user) && !isTokenProject"
            [matTooltip]="earningTooltipMessage"
            [matTooltipDisabled]="!showTooltip"
            [compact]="true"
            [earnings]="
              isSearchCard(user)
                ? freelancerReputation?.earningsScore
                : user.reputation?.earningsScore
            "
            [flMarginBottom]="'xxxsmall'"
            [flMarginBottomDesktop]="'none'"
            [flMarginRight]="'xxsmall'"
            [size]="IconSize.SMALL"
            [sizeDesktop]="IconSize.MID"
          ></fl-earnings>
          <!-- Hide completion rate on Deloitte internal projects -->
          <fl-completed-jobs
            *ngIf="
              freelancerReputation && !(isDeloitteProject && isTokenProject)
            "
            i18n-matTooltip="Jobs percentage tooltip message"
            matTooltip="Percentage of successfully completed jobs."
            [matTooltipDisabled]="!showTooltip"
            [completeCounter]="
              freelancerReputation?.entireHistory.completionRate
            "
            [flMarginBottom]="'xxxsmall'"
            [flMarginBottomDesktop]="'none'"
            [flMarginRight]="'xxsmall'"
            [size]="IconSize.SMALL"
            [sizeDesktop]="IconSize.MID"
          ></fl-completed-jobs>
        </ng-container>
        <ng-container
          *ngIf="isNewFreelancer && !isBidder && showNewFreelancerLabel"
        >
          <div
            class="NewFreelancer"
            [flMarginRight]="'xxsmall'"
          >
            <fl-icon
              class="NewFreelancerIcon"
              [color]="IconColor.INHERIT"
              [size]="IconSize.SMALL"
              [sizeDesktop]="IconSize.MID"
              [name]="'ui-star-v2'"
              [flMarginRight]="'xxxsmall'"
            ></fl-icon>
            <p
              class="text-xxsmall desktop:text-small"
              i18n="New Freelancer user"
            >
              New Freelancer
            </p>
          </div>
        </ng-container>
        <div
          *ngIf="showCountry && user.location"
          class="UserLocation"
        >
          <fl-flag
            [flMarginBottom]="'xxxsmall'"
            [flMarginBottomDesktop]="'none'"
            [flMarginRight]="'xxsmall'"
            [countryCode]="user.location?.country?.code"
            [size]="FlagSize.MID"
          ></fl-flag>
          <p
            class="text-xxsmall desktop:text-small"
            i18n="Indicates city and country location of the user"
          >
            <ng-container *ngIf="showCity && isShortCityName">
              {{ user.location?.city | titlecase }},
            </ng-container>
            {{ this.user.location?.country?.name | titlecase }}
          </p>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./user-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserInfoComponent implements OnChanges, OnInit {
  FlagSize = FlagSize;
  FontType = FontType;
  FontWeight = FontWeight;
  IconColor = IconColor;
  IconSize = IconSize;
  TextSize = TextSize;
  UsernameSize = UsernameSize;

  @Input() freelancerReputation?: FreelancerReputation;
  @Input() isDeloitteProject?: boolean;
  @Input() isTokenProject?: boolean;
  @Input() ratingControl = new FormControl(0, { nonNullable: true });
  @Input() showCity? = true;
  @Input() showCountry? = true;
  @Input() showNewFreelancerLabel?: boolean = true;
  @Input() showTooltip = true;
  @Input() showEarningScore? = true;
  @Input() user: ProjectViewUser | SearchUser | CategoryPageViewUser;
  @Input() userBadges?: UsernameBadges;
  @Input() userLink?: string;
  @Input() userLinkQueryParams?: QueryParams;
  @Input() usernameMarginBottomDesktop: Margin = 'xxxsmall';
  @Input() usernameSize: UsernameSize;
  @Input() usernameSizeDesktop: UsernameSize;

  // To be used just in the bid-pattern-card to correct view of the card.yep
  @Input() isBidder? = true;

  ratingTooltipMessage = '';
  earningTooltipMessage = '';
  reviewTooltipMessage = $localize`Total number of reviews received.`;
  isShortCityName: boolean;
  readonly CITY_LENGTH_LIMIT = 10;
  isNewFreelancer = false;

  ngOnInit(): void {
    if (this.isBidCard(this.user)) {
      this.isNewFreelancer = !!this.user.isNewFreelancer;
    }

    if (this.isSearchCard(this.user)) {
      this.isNewFreelancer = this.user.reviews === 0 && this.user.rating === 0;
      this.isBidder = false;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.showTooltip) {
      if (this.isBidCard(this.user)) {
        this.ratingTooltipMessage = $localize`Overall rating for projects with the same skills as this project`;
        this.earningTooltipMessage = $localize`Total amount earned by this freelancer in the skills of this project (out of 10.0).`;
      } else {
        this.ratingTooltipMessage = $localize`Overall rating of the user`;
        this.earningTooltipMessage = $localize`Earning score out of 10 for doing jobs in this skill or category. Increases as jobs are successfully completed and paid through the site.`;
      }
    } else {
      this.ratingTooltipMessage = '';
      this.earningTooltipMessage = '';
      this.reviewTooltipMessage = '';
    }

    if (this.isSearchCard(this.user) && this.user.location?.city) {
      if (this.user.location?.city.length >= this.CITY_LENGTH_LIMIT) {
        this.isShortCityName = false;
      } else {
        this.isShortCityName = true;
      }
    } else {
      this.isShortCityName = true;
    }
  }

  isBidCard(
    user: ProjectViewUser | SearchUser | CategoryPageViewUser,
  ): user is ProjectViewUser {
    return 'displayName' in user && 'employerReputation' in user;
  }

  isSearchCard(
    user: ProjectViewUser | SearchUser | CategoryPageViewUser,
  ): user is SearchUser {
    return 'reviews' in user;
  }

  hasReputation(
    user: ProjectViewUser | SearchUser | CategoryPageViewUser,
  ): user is ProjectViewUser | SearchUser {
    return 'reputation' in user;
  }
}
